import { useEffect } from "react";

/**
 * Custom React hook to lock the body scroll when `enabled` is true.
 *
 * When enabled, sets `overflow: hidden` on the body to prevent scrolling,
 * and adds right padding equal to the scrollbar width to prevent layout shift.
 * Cleans up by restoring previous styles when disabled or on unmount.
 */
export function useBodyScrollLock(enabled: boolean) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Get the body style object
    const { style } = document.body as HTMLBodyElement;
    // Store previous overflow and paddingRight values to restore later
    const prevOverflow = style.overflow;
    const prevPaddingRight = style.paddingRight;

    // Calculate the width of the scrollbar
    const scrollBarGap =
      window.innerWidth - document.documentElement.clientWidth;

    // Lock scroll by hiding overflow
    style.overflow = "hidden";
    // Add padding to compensate for scrollbar disappearance
    if (scrollBarGap > 0) {
      style.paddingRight = `${scrollBarGap}px`;
    }

    // Cleanup: restore previous styles
    return () => {
      style.overflow = prevOverflow;
      style.paddingRight = prevPaddingRight;
    };
  }, [enabled]);
}
